import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, CheckCircle2, Loader2, Mail, Calendar } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyReportOptIn({ url, email, name }) {
  const [subscribed, setSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!url || !email) { setChecking(false); return; }
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`, { params: { url, email } });
        if (res.data.subscribed) {
          setSubscribed(true);
          setSubscription(res.data.subscription);
        }
      } catch {
        // ignore
      } finally {
        setChecking(false);
      }
    };
    checkStatus();
  }, [url, email]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { url, email, name });
      setSubscribed(true);
      setSubscription(res.data.subscription);
    } catch (e) {
      console.error("Subscribe failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!subscription?.id) return;
    setLoading(true);
    try {
      await axios.post(`${API}/unsubscribe`, { subscription_id: subscription.id });
      setSubscribed(false);
      setSubscription(null);
    } catch (e) {
      console.error("Unsubscribe failed:", e);
    } finally {
      setLoading(false);
    }
  };

  if (checking) return null;

  const nextScan = subscription?.next_scan_at
    ? new Date(subscription.next_scan_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-10"
      data-testid="weekly-report-section"
    >
      <AnimatePresence mode="wait">
        {!subscribed ? (
          <motion.div
            key="unsubscribed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="border border-zinc-800 rounded-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#1a1a1c] to-[#0f0f10] p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-14 h-14 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Bell className="w-7 h-7 text-orange-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Get Weekly Visibility Reports
                </h3>
                <p className="text-sm text-zinc-400 mb-1">
                  We'll automatically re-scan your site every week and email you a report showing how your scores changed.
                  Track your progress over time — no effort required.
                </p>
                <div className="flex items-center gap-4 text-xs text-zinc-500 mt-2">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Sent to {email}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Every Monday</span>
                </div>
              </div>
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="flex items-center gap-2 bg-orange-500 text-white font-bold py-3 px-6 rounded-md transition-all duration-300 hover:bg-orange-600 shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] disabled:opacity-50 whitespace-nowrap"
                data-testid="subscribe-button"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing...</>
                ) : (
                  <><Bell className="w-4 h-4" /> Enable Weekly Reports</>
                )}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="subscribed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="border border-green-500/20 rounded-xl bg-green-500/5 p-6"
          >
            <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
              <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-zinc-300 font-semibold">Weekly reports enabled!</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  We'll re-scan your site and send a report to <span className="text-zinc-400">{email}</span> every week.
                  {nextScan && <> Next scan: <span className="text-orange-500">{nextScan}</span></>}
                </p>
              </div>
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-50"
                data-testid="unsubscribe-button"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellOff className="w-3 h-3" />}
                Unsubscribe
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
